import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  kanbanApi,
  type KanbanSortBy,
  kanbanSortBySchema,
  SetMyKanbanSettingsApiArg,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useGetUsersQuery} from '@dms/api/customer';
import {selectUserBranchOptions} from '@dms/api/features';
import {type BranchOptionsItem, UserStateFilterSchema} from '@dms/api/shared';
import {handleApiError} from '@dms/shared';

import {
  ApiError,
  isNotNilOrEmpty,
  type Nullish,
  pushGtmEvent,
  useDebouncedCallback,
} from 'shared/web';

import {KANBAN_DEFAULT_SORT_BY} from '../constants/kanbanDefaultSortBy';
import {KANBAN_DEFAULT_VISIBLE_COLUMNS} from '../constants/kanbanDefaultVisibleColumns';
import {OPPORTUNITIES_KANBAN_NAME} from '../constants/opportunitiesKanbanName';
import {KanbanSettings} from '../types/KanbanSettings';
import {getKanbanUsersFromUsers} from '../utils/getKanbanUsersFromUsers';
import {getKanbanUsersInBranches} from '../utils/getKanbanUsersInBranches';
import {getNonHiddenSelectedUserIds} from '../utils/getNonHiddenSelectedUserIds';
import {getSelectedKanbanUsers} from '../utils/getSelectedKanbanUsers';

type UseKanbanSettingsParams = (
  nonHiddenSelectedUserIds: string[],
  sortBy: KanbanSortBy,
  branchIds: string[]
) => void;

export function useKanbanSettings(onFilterChange: UseKanbanSettingsParams) {
  const {data: currentUserData} = useGetCurrentUserInfoQuery();
  const branchOptions = useSelector(selectUserBranchOptions);

  const {
    data: users,
    isLoading: isUsersLoading,
    isError: isUsersErrored,
  } = useGetUsersQuery(
    {stateFilter: UserStateFilterSchema.enum.active},
    {
      skip: !currentUserData,
    }
  );

  const {data: settings} = kanbanApi.useGetMyKanbanSettingsQuery(
    {
      kanbanName: OPPORTUNITIES_KANBAN_NAME,
    },
    {
      skip: !currentUserData,
    }
  );

  const [setMyKanbanSettings] = kanbanApi.useSetMyKanbanSettingsMutation();

  const [kanbanSettings, setKanbanSettings] = useState<KanbanSettings>({
    selectedUsers: [],
    visibleColumns: [],
    sortBy: KANBAN_DEFAULT_SORT_BY,
    branchIds: [],
  });

  const selectedUsersLoadingFallback = kanbanSettings && kanbanSettings.selectedUsers.length === 0;

  useEffect(
    function applyStoredKanbanSettings() {
      if (
        isNotNil(settings?.selectedUsers) &&
        isNotNil(users) &&
        isNotNil(currentUserData) &&
        isNotNil(branchOptions) &&
        selectedUsersLoadingFallback
      ) {
        const branchIds = getAccessibleBranchIds(settings.branchIds, branchOptions);
        const kanbanUsers = getKanbanUsersInBranches(
          getSelectedKanbanUsers({
            users,
            selectedUsers: settings.selectedUsers,
            currentUserData,
          }),
          branchIds,
          currentUserData.id
        );
        const parsedSortBy = kanbanSortBySchema.safeParse(settings.sortBy);
        const sortBy = parsedSortBy.success ? parsedSortBy.data : KANBAN_DEFAULT_SORT_BY;

        setKanbanSettings({
          selectedUsers: kanbanUsers,
          // The fallback to KANBAN_DEFAULT_VISIBLE_COLUMNS guarantees visibleColumns[0]
          // is always defined at runtime, ensuring safety of array access pattern
          visibleColumns: isNotNilOrEmpty(settings?.visibleColumns?.[0])
            ? settings.visibleColumns
            : KANBAN_DEFAULT_VISIBLE_COLUMNS,
          sortBy,
          branchIds,
        });
        onFilterChange(getNonHiddenSelectedUserIds(kanbanUsers), sortBy, branchIds);
      }
    },
    [settings, users, currentUserData, branchOptions, selectedUsersLoadingFallback, onFilterChange]
  );

  const debouncedKanbanSettingsChange = useDebouncedCallback(
    async (values: KanbanSettings) => {
      const selectedUsers = values.selectedUsers.map((user) => ({
        userId: user.id,
        isHidden: user.isHidden,
      }));

      const args: SetMyKanbanSettingsApiArg = {
        kanbanName: OPPORTUNITIES_KANBAN_NAME,
        setMyKanbanSettingsRequestBody: {
          selectedUsers,
          visibleColumns: values.visibleColumns,
          sortBy: values.sortBy,
          branchIds: values.branchIds,
        },
      };

      return await setMyKanbanSettings(args)
        .unwrap()
        .then(() => {
          onFilterChange(
            getNonHiddenSelectedUserIds(values.selectedUsers),
            values.sortBy,
            values.branchIds
          );
        })
        .then(() => {
          pushGtmEvent({
            event: 'button_click',
            event_id: 'opportunities_kanban_settings_change',
          });
        })
        .catch((error: ApiError) => {
          const parsedRevertSortBy = kanbanSortBySchema.safeParse(settings?.sortBy);
          const revertedBranchIds = getAccessibleBranchIds(settings?.branchIds, branchOptions);
          const revertedSettings: KanbanSettings = {
            selectedUsers: getKanbanUsersInBranches(
              getSelectedKanbanUsers({
                users,
                selectedUsers: settings?.selectedUsers,
                currentUserData,
              }),
              revertedBranchIds,
              currentUserData?.id
            ),
            // The fallback to KANBAN_DEFAULT_VISIBLE_COLUMNS guarantees visibleColumns[0]
            // is always defined at runtime, ensuring safety of array access pattern
            visibleColumns: isNotNilOrEmpty(settings?.visibleColumns?.[0])
              ? settings.visibleColumns
              : KANBAN_DEFAULT_VISIBLE_COLUMNS,
            sortBy: parsedRevertSortBy.success ? parsedRevertSortBy.data : KANBAN_DEFAULT_SORT_BY,
            branchIds: revertedBranchIds,
          };
          setKanbanSettings(revertedSettings);
          onFilterChange(
            getNonHiddenSelectedUserIds(revertedSettings.selectedUsers),
            revertedSettings.sortBy,
            revertedSettings.branchIds
          );
          handleApiError(error);
        });
    },
    DEBOUNCE_DELAY,
    DEBOUNCE_MAX_WAIT
  );

  const isLoading = isUsersLoading || selectedUsersLoadingFallback;
  const isError = isUsersErrored;

  return {
    currentUser: currentUserData,
    users: getKanbanUsersFromUsers(users),
    settings: kanbanSettings,
    isLoading,
    isError,
    onKanbanSettingsChange: (values: KanbanSettings) => {
      setKanbanSettings(values);
      debouncedKanbanSettingsChange(values);
    },
  };
}

const DEBOUNCE_DELAY = 700;
const DEBOUNCE_MAX_WAIT = 2000;

function getAccessibleBranchIds(
  branchIds: string[] | Nullish,
  branchOptions: BranchOptionsItem[] | Nullish
) {
  if (isNil(branchIds) || isNil(branchOptions)) {
    return [];
  }

  return branchIds.filter((branchId) => branchOptions.some((option) => option.value === branchId));
}
